/**
 * Copyright (c) 2010-2023 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { MapInteractionsService } from '@map/services/mapInteractions.service';
import { Store } from '@ngxs/store';
import { filter, Observable, race, Subject, Subscription, take, takeUntil, tap } from 'rxjs';
import { SelectionState } from '@map/state/selection.state';
import { mergeMap } from 'rxjs/operators';
import { MapCommand } from '../../map-command.class';
import { BufferDialogComponent } from './dialog/buffer-dialog.component';

export class CreateBufferCommand extends MapCommand {
    hasSelection$: Observable<boolean> = inject(Store).select(SelectionState.hasSelection);

    constructor() {
        super();
    }

    invoke() {
        const snackCancelled = new Subject<void>();
        let cancelSub: Subscription;
        let snackRef: MatSnackBarRef<TextOnlySnackBar>;
        const needsSelection$ = this.hasSelection$.pipe(
            filter(hasSelection => !hasSelection),
            tap(() => (this.deps['interactions'] as MapInteractionsService).enablePanInteraction()),
            tap(() => {
                snackRef = this.deps['snackBar'].open('Select features to buffer', 'Cancel');
                cancelSub = snackRef.onAction().pipe(take(1)).subscribe(() => snackCancelled.next());
            }),
            mergeMap(() => this.hasSelection$),
            filter(hasSelection => hasSelection),
            tap(() => snackRef.dismiss()),
            takeUntil(snackCancelled),
            take(1)
        );
        const hasSelection$ = this.hasSelection$.pipe(
            filter(hasSelection => hasSelection),
            take(1),
        );

        race(needsSelection$, hasSelection$).pipe(
            tap(s => this.deps['logger'].debug('has selection?', s)),
            take(1),
        ).subscribe(() => {
            if (cancelSub) {
                cancelSub.unsubscribe();
            }
            const matDialog: MatDialog = this.deps['matDialog'];
            matDialog
                .open(BufferDialogComponent)
                .afterClosed()
                .subscribe(() => this.deactivate$.next());
        });
    }

    setDependencies(deps: { [p: string]: any }) {
        super.setDependencies(deps);
    }
}
